import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Divider,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  Collapse,
  Link
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { initiateOAuthLogin } from '../auth/directOAuthHandler';
import { requestAccess } from '../graphql';

const ID_LOCKER_URL = 'https://idlocker.nike.com';

interface LoginPageProps {
  error?: string | null;
  errorDescription?: string | null;
  userEmail?: string | null;
}

const LoginPage: React.FC<LoginPageProps> = ({ error, errorDescription, userEmail }) => {
  const [email, setEmail] = useState(userEmail || '');
  const [comment, setComment] = useState('');
  const [requestStatus, setRequestStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [requestMessage, setRequestMessage] = useState('');

  const handleLogin = () => {
    initiateOAuthLogin();
  };

  const handleRequestAccess = async () => {
    if (!email || !email.includes('@')) {
      setRequestStatus('error');
      setRequestMessage('Please enter a valid Nike email address.');
      return;
    }

    setRequestStatus('loading');
    setRequestMessage('');

    try {
      const result = await requestAccess(email, comment || undefined);
      if (result.success) {
        setRequestStatus('success');
        setRequestMessage(result.message);
      } else {
        setRequestStatus('error');
        setRequestMessage(result.message);
      }
    } catch (err: any) {
      setRequestStatus('error');
      setRequestMessage(err.message || 'Failed to submit access request. Please try via ID Locker.');
    }
  };

  const hasError = !!error;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f5f5f5',
        px: 2,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Paper
          elevation={3}
          sx={{ borderRadius: 3, overflow: 'hidden' }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', pt: 3, pb: 2, px: 4, backgroundColor: '#fff' }}>
            <Box sx={{ mb: 2 }}>
              <img
                src="/logo.png"
                alt="Nike"
                style={{ height: '44px', width: 'auto' }}
              />
            </Box>

            {hasError && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1.5,
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                  Unable to access — {errorDescription || error}
                </Typography>
              </Box>
            )}

            <Button
              variant="contained"
              onClick={handleLogin}
              sx={{
                width: '50%',
                py: 1,
                fontSize: '15px',
                textTransform: 'none',
                backgroundColor: '#2196F3',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1976D2' }
              }}
            >
              Login
            </Button>
          </Box>

          <Divider />

          {/* Access request section */}
          <Box sx={{ px: 4, py: 2.5, backgroundColor: '#fafafa' }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: '#333', mb: 1.5, textAlign: 'center' }}
            >
              Don't have access? Request below
            </Typography>

            {requestStatus !== 'success' && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#fff',
                  border: '1px solid #e0e0e0'
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Nike email"
                  placeholder="yourname@nike.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={requestStatus === 'loading'}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Justification (optional)"
                  placeholder="Why do you need access?"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  disabled={requestStatus === 'loading'}
                  sx={{ mb: 1.5 }}
                />
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleRequestAccess}
                  disabled={requestStatus === 'loading' || !email}
                  startIcon={requestStatus === 'loading' ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                  sx={{
                    py: 0.8,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '14px',
                    backgroundColor: '#4caf50',
                    '&:hover': { backgroundColor: '#388e3c' }
                  }}
                >
                  {requestStatus === 'loading' ? 'Submitting...' : 'Request Access'}
                </Button>

                <Collapse in={requestStatus === 'error'}>
                  <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>
                    {requestMessage || 'Request failed. Try via ID Locker.'}
                  </Alert>
                </Collapse>
              </Box>
            )}

            <Collapse in={requestStatus === 'success'}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#e8f5e9',
                  border: '1px solid #a5d6a7'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CheckCircleOutlineIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                    Request Submitted Successfully
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: '#333', lineHeight: 1.6 }}>
                  You will receive an email once your request is approved. After approval, come back and click <strong>Login</strong> to access the dashboard.
                </Typography>
              </Box>
            </Collapse>

            <Typography
              variant="caption"
              sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: '#888' }}
            >
              Or request manually via{' '}
              <Link href={ID_LOCKER_URL} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
                ID Locker
              </Link>
              {' '}— search for <strong>App.QrRedirectAutomation.User</strong>
            </Typography>
          </Box>
        </Paper>

        <Typography
          variant="caption"
          sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: '#aaa' }}
        >
          Need help? Contact the Connected Products team.
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
